<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import type { DataTableColumns, DropdownOption } from 'naive-ui';
import type {
  ClauseSlim,
  ContractManagementContractInDbSlim,
  ContractTypeUsage,
  ExtractionPromptSearchEntry,
  PlaybookInDb,
} from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import { IconMore } from '@/components/design/icons';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaClippedText from '@/components/design/base/GaiaClippedText.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import GaiaContractTypeDeleteModal from '@/components/design/domain/contractTypes/GaiaContractTypeDeleteModal.vue';
import GaiaContractTypeFormModal from '@/components/design/domain/contractTypes/GaiaContractTypeFormModal.vue';
import { clauseCategoryMap, playbookPartyPositionMap } from '@/components/design/statusMaps';
import AddExistingPromptsPanel from '@/components/contractManagement/AddExistingPromptsPanel.vue';
import ExtractionPromptDrawer from '@/components/contractManagement/ExtractionPromptDrawer.vue';
import ExtractionPromptTable from '@/components/contractManagement/ExtractionPromptTable.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { contractRoute, playbookRunsRoute } from '@/router/links';
import { ContractTypesService } from '@/services/contractManagement/contractTypesService';
import { ExtractionPromptsService } from '@/services/contractManagement/extractionPromptsService';
import { useAuthStore } from '@/stores/auth';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { logger } from '@/utils/logger';

/**
 * One contract type: the extraction prompts views pick from it, and the
 * contracts, playbooks and clauses that reference it. Each is a tab; the tab,
 * page and search live in the URL. Only an org admin can change anything.
 */

type ContractTypeTab = 'prompts' | 'contracts' | 'playbooks' | 'clauses';

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();
const contractTypeStore = useContractTypeStore();

const typeUid = computed(() => route.params.uid as string);
const canWrite = computed(() => authStore.userPermissions.is_org_admin);

// The store holds the org's whole list; there is no single-type fetch.
const contractType = computed(() =>
  contractTypeStore.contractTypes.find(type => type.uid === typeUid.value)
);

const { activeTab, currentPage, pageSize, searchQuery } = useListQuerySync({
  defaultTab: 'prompts',
  allowedTabs: ['prompts', 'contracts', 'playbooks', 'clauses'],
});

const currentTab = computed(() => activeTab.value as ContractTypeTab);

// ==================== Counts ====================

const usage = ref<ContractTypeUsage | null>(null);

async function fetchUsage(): Promise<void> {
  try {
    usage.value = await ContractTypesService.usage(typeUid.value);
  } catch (error) {
    logger.error('Failed to load contract type usage:', error);
  }
}

const tabs = computed((): GaiaTabBarItem[] => [
  { key: 'prompts', label: 'Prompts', count: usage.value?.extraction_prompt_count },
  { key: 'contracts', label: 'Contracts', count: usage.value?.contract_count },
  { key: 'playbooks', label: 'Playbooks', count: usage.value?.playbook_count },
  { key: 'clauses', label: 'Clauses', count: usage.value?.clause_count },
]);

const searchPlaceholders: Record<ContractTypeTab, string> = {
  prompts: 'Search prompts',
  contracts: 'Search contracts',
  playbooks: 'Search playbooks',
  clauses: 'Search clauses',
};

// ==================== Rows (server-paged, one tab at a time) ====================

const prompts = ref<ExtractionPromptSearchEntry[]>([]);
const contracts = ref<ContractManagementContractInDbSlim[]>([]);
const playbooks = ref<PlaybookInDb[]>([]);
const clauses = ref<ClauseSlim[]>([]);
const total = ref(0);
const rowsLoading = ref(false);
let latestRequest = 0;

async function fetchRows(): Promise<void> {
  const request = ++latestRequest;
  const tab = currentTab.value;
  const uid = typeUid.value;
  const page = {
    text: searchQuery.value.trim(),
    offset: (currentPage.value - 1) * pageSize.value,
    limit: pageSize.value,
  };
  rowsLoading.value = true;
  try {
    let count: number;
    switch (tab) {
      case 'prompts': {
        const result = await ExtractionPromptsService.search({
          filter: { contractTypeUids: [uid] },
          ...page,
        });
        if (request !== latestRequest) return;
        prompts.value = result.results;
        count = result.result_count;
        break;
      }
      case 'contracts': {
        const result = await ContractTypesService.searchContracts(uid, page);
        if (request !== latestRequest) return;
        contracts.value = result.results;
        count = result.result_count;
        break;
      }
      case 'playbooks': {
        const result = await ContractTypesService.searchPlaybooks(uid, page);
        if (request !== latestRequest) return;
        playbooks.value = result.results;
        count = result.result_count;
        break;
      }
      default: {
        const result = await ContractTypesService.searchClauses(uid, page);
        if (request !== latestRequest) return;
        clauses.value = result.results;
        count = result.result_count;
      }
    }
    total.value = count;
  } catch (error) {
    if (request !== latestRequest) return;
    message.error(error instanceof Error ? error.message : 'Failed to load the list');
    total.value = 0;
  } finally {
    if (request === latestRequest) rowsLoading.value = false;
  }
}

const pagination = usePaginationConfig(currentPage, pageSize, total);

// usePaginationConfig only updates the refs; the fetch follows them.
watch([currentTab, currentPage, pageSize], () => {
  void fetchRows();
});

function applySearch(): void {
  if (currentPage.value === 1) {
    void fetchRows();
  } else {
    currentPage.value = 1;
  }
}

function selectTab(key: string): void {
  if (key === activeTab.value) return;
  searchQuery.value = '';
  currentPage.value = 1;
  total.value = 0;
  activeTab.value = key;
}

/** After a prompt is added, changed or deleted: the list and the tab counts. */
function refreshPrompts(): void {
  void fetchRows();
  void fetchUsage();
}

// ==================== Contract type actions ====================

const showTypeForm = ref(false);
const showTypeDelete = ref(false);

const typeMenuOptions: DropdownOption[] = [
  { key: 'delete', label: 'Delete contract type', props: gaiaDangerOption },
];

function onTypeMenu(key: string | number): void {
  if (key === 'delete') showTypeDelete.value = true;
}

// The delete modal drops the type from the store; there is nothing left to show here.
watch(contractType, (current, previous) => {
  if (previous && !current) void router.push({ name: 'contract-types' });
});

// ==================== Adding a prompt ====================

const showPromptDrawer = ref(false);
/** The prompt the panel edits; null while it creates one. */
const editedPrompt = ref<ExtractionPromptSearchEntry | null>(null);
const showAddExisting = ref(false);

const addPromptOptions: DropdownOption[] = [
  { key: 'existing', label: 'Add existing prompt' },
  { key: 'new', label: 'Create new prompt' },
];

// Both open in the page's side panel, one at a time.
function onAddPromptMenu(key: string | number): void {
  showAddExisting.value = key === 'existing';
  showPromptDrawer.value = key !== 'existing';
  editedPrompt.value = null;
}

function openEditPrompt(prompt: ExtractionPromptSearchEntry): void {
  showAddExisting.value = false;
  editedPrompt.value = prompt;
  showPromptDrawer.value = true;
}

const sidePanelOpen = computed(() => showAddExisting.value || showPromptDrawer.value);

// ==================== Columns ====================

/** A name that links, without the row's own click also firing. */
function renderLink(to: ReturnType<typeof contractRoute>, name: string): VNode {
  return h(
    RouterLink,
    {
      to,
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
      },
    },
    () => h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => name)
  );
}

const contractColumns: DataTableColumns<ContractManagementContractInDbSlim> = [
  {
    title: 'Name',
    key: 'name',
    render: row => renderLink(contractRoute(row.uid), row.name),
  },
  {
    title: 'Status',
    key: 'states',
    width: 220,
    render: row => h(GaiaContractStateCell, { states: row.states ?? null }),
  },
  {
    title: 'Owner',
    key: 'owner_user',
    width: 200,
    render: row =>
      h(GaiaText, { size: 'dense', tone: 'subtle' }, () =>
        row.owner_user ? row.owner_user.full_name || row.owner_user.email : '—'
      ),
  },
  {
    title: 'Updated',
    key: 'updated_at',
    width: 180,
    render: row =>
      h(GaiaText, { size: 'dense', tone: 'subtle' }, () =>
        formatDateTimeForTable(row.updated_at ?? row.created_at)
      ),
  },
];

const playbookColumns: DataTableColumns<PlaybookInDb> = [
  {
    title: 'Name',
    key: 'name',
    render: row => renderLink(playbookRunsRoute(row.uid), row.name),
  },
  {
    title: 'Position',
    key: 'party_position',
    width: 160,
    render: row =>
      h(
        GaiaText,
        { size: 'dense', tone: 'subtle' },
        () => playbookPartyPositionMap[row.party_position]
      ),
  },
  {
    title: 'Updated',
    key: 'updated_at',
    width: 180,
    render: row =>
      h(GaiaText, { size: 'dense', tone: 'subtle' }, () =>
        formatDateTimeForTable(row.updated_at ?? row.created_at)
      ),
  },
];

const clauseColumns: DataTableColumns<ClauseSlim> = [
  {
    title: 'Name',
    key: 'name',
    render: row =>
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 } }, [
        h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => row.name),
        h(GaiaClippedText, { text: row.content, max: 120 }),
      ]),
  },
  {
    title: 'Category',
    key: 'clause_category',
    width: 180,
    render: row =>
      h(GaiaText, { size: 'dense', tone: 'subtle' }, () =>
        row.clause_category ? clauseCategoryMap[row.clause_category] : '—'
      ),
  },
  {
    title: 'Jurisdiction',
    key: 'jurisdiction',
    width: 140,
    render: row => h(GaiaText, { size: 'dense', tone: 'subtle' }, () => row.jurisdiction),
  },
  {
    title: 'Language',
    key: 'language',
    width: 120,
    render: row => h(GaiaText, { size: 'dense', tone: 'subtle' }, () => row.language),
  },
];

const emptyTitles: Record<ContractTypeTab, string> = {
  prompts: 'No prompts yet',
  contracts: 'No contracts for this contract type',
  playbooks: 'No playbooks for this contract type',
  clauses: 'No clauses for this contract type',
};

const showEmptyState = computed(
  () => !rowsLoading.value && total.value === 0 && !searchQuery.value.trim()
);

onMounted(() => {
  void contractTypeStore.ensureLoaded();
  void fetchUsage();
  void fetchRows();
});
</script>

<template>
  <!-- Adding existing prompts opens beside the list rather than over it: the layout's drawer
       squeezes the page, so the type's prompts stay in view. -->
  <Layout
    :content-max-width="gaiaContentWidth.table"
    :drawer-open="sidePanelOpen"
    :drawer-width="580"
  >
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'contract-types' }">{{ navLabels.contractTypes }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ contractType?.name ?? 'Contract type' }}</n-breadcrumb-item>
    </template>

    <n-spin :show="contractTypeStore.loading && !contractType">
      <template v-if="contractType">
        <!-- Header: what the type is, and what you do to it -->
        <n-flex vertical :size="16" :wrap="false" :style="{ marginBottom: '20px' }">
          <n-flex justify="space-between" align="flex-start" :size="16" :wrap="false">
            <n-flex vertical :size="6" :style="{ minWidth: 0, flex: '1 1 auto' }">
              <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
                {{ contractType.name }}
              </gaia-header>
              <gaia-text
                v-if="contractType.description"
                size="body"
                tone="subtle"
                :style="{ maxWidth: '64ch' }"
              >
                {{ contractType.description }}
              </gaia-text>
            </n-flex>
            <n-flex
              v-if="canWrite"
              align="center"
              :size="8"
              :wrap="false"
              :style="{ flex: 'none' }"
            >
              <gaia-button type="primary" @click="showTypeForm = true">Edit</gaia-button>
              <n-dropdown
                trigger="click"
                placement="bottom-end"
                :options="typeMenuOptions"
                @select="onTypeMenu"
              >
                <gaia-button aria-label="More actions" title="More actions">
                  <template #icon>
                    <n-icon :component="IconMore" />
                  </template>
                </gaia-button>
              </n-dropdown>
            </n-flex>
          </n-flex>
          <gaia-tab-bar :items="tabs" :active="currentTab" @select="selectTab" />
        </n-flex>

        <gaia-empty-state v-if="showEmptyState" :title="emptyTitles[currentTab]" dashed>
          <template v-if="currentTab === 'prompts'">
            Views pick a column's question from their contract types' prompts.
          </template>
          <template v-if="currentTab === 'prompts' && canWrite" #actions>
            <gaia-dropdown :options="addPromptOptions" @select="onAddPromptMenu">
              <gaia-add-button subject="prompt" :icon="false" />
            </gaia-dropdown>
          </template>
        </gaia-empty-state>

        <template v-else>
          <n-flex
            justify="space-between"
            align="center"
            :size="12"
            :style="{ marginBottom: '12px' }"
          >
            <gaia-search-input
              v-model:value="searchQuery"
              :placeholder="searchPlaceholders[currentTab]"
              :debounce="500"
              style="width: 260px"
              @search="applySearch"
            />
            <gaia-dropdown
              v-if="currentTab === 'prompts' && canWrite"
              placement="bottom-end"
              :options="addPromptOptions"
              @select="onAddPromptMenu"
            >
              <gaia-add-button subject="prompt" />
            </gaia-dropdown>
          </n-flex>

          <extraction-prompt-table
            v-if="currentTab === 'prompts'"
            :prompts="prompts"
            :loading="rowsLoading"
            :pagination="pagination"
            :can-write="canWrite"
            :contract-type-uid="typeUid"
            :truncate-prompt="sidePanelOpen"
            @changed="refreshPrompts"
            @edit="openEditPrompt"
          />
          <gaia-table
            v-else-if="currentTab === 'contracts'"
            :columns="contractColumns"
            :data="contracts"
            :loading="rowsLoading"
            remote
            :pagination="pagination"
            :row-key="(row: ContractManagementContractInDbSlim) => row.uid"
          />
          <gaia-table
            v-else-if="currentTab === 'playbooks'"
            :columns="playbookColumns"
            :data="playbooks"
            :loading="rowsLoading"
            remote
            :pagination="pagination"
            :row-key="(row: PlaybookInDb) => row.uid"
          />
          <gaia-table
            v-else
            :columns="clauseColumns"
            :data="clauses"
            :loading="rowsLoading"
            remote
            :pagination="pagination"
            :row-key="(row: ClauseSlim) => row.uid"
          />
        </template>
      </template>

      <gaia-empty-state v-else-if="contractTypeStore.loaded" title="Contract type not found">
        It may have been deleted.
        <template #actions>
          <gaia-button @click="router.push({ name: 'contract-types' })">
            Back to contract types
          </gaia-button>
        </template>
      </gaia-empty-state>
    </n-spin>

    <gaia-contract-type-form-modal
      v-model:show="showTypeForm"
      :contract-type="contractType ?? null"
    />
    <gaia-contract-type-delete-modal
      v-model:show="showTypeDelete"
      :contract-type="contractType ?? null"
    />

    <!-- Both stay mounted (they reset when `show` turns on) and show one at a time. -->
    <template #drawer>
      <div v-show="showAddExisting" :style="{ height: '100%' }">
        <add-existing-prompts-panel
          v-model:show="showAddExisting"
          :contract-type-uid="typeUid"
          :contract-type-name="contractType?.name ?? 'this contract type'"
          @added="refreshPrompts"
        />
      </div>
      <div v-show="showPromptDrawer" :style="{ height: '100%' }">
        <extraction-prompt-drawer
          v-model:show="showPromptDrawer"
          inline
          :prompt="editedPrompt"
          :default-contract-type-uids="[typeUid]"
          @saved="refreshPrompts"
        />
      </div>
    </template>
  </Layout>
</template>
